import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { Page } from '@shapeable/gesda-types';
import { SliceLayoutBoundary, SiteHeaderLayout, EntityBannerProps, ExplorerEntityDetailsLayoutDefaultProps, ExplorerEntityDetailsLayout, ExplorerEntityDetailsLayoutProps, ComponentPropsProvider, PAGE_HOME, BackButtonEntityProvider, SvgCanvasViewBoxDefinition, DownloadButton, useLang, ExplorerTabs, ActiveEntityProvider } from '@shapeable/ui';
import { PageMetaLayout } from './page-meta-layout';
import { useRadar } from '@shapeable/gesda-ui';
import { ExplorerProvider } from '../providers/explorer-provider';
import { RadarChartMain } from '../elements/radar-chart-main';
import { RadarChart } from '../elements/radar-chart';
import { GESDA_PURPLE_DARK } from '../../theme';
const cls = classNames('page-explorer-layout');

// -------- Types -------->

type RadarProps = {
  isInteractive: boolean;
}

// -------- Props -------->

export type PageExplorerLayoutProps = ExplorerEntityDetailsLayoutProps & {  
  entity?: Page;
  bannerProps?: Omit<EntityBannerProps, 'entity'>;
  showRadar?: boolean;
  isAsideSticky?: boolean;
};

export const PageExplorerLayoutDefaultProps: Omit<PageExplorerLayoutProps, 'entity'> = {
  ...ExplorerEntityDetailsLayoutDefaultProps,
  isAsideSticky: false,
  showRadar: true,
};

// -------- Child Component Props -------->

type ContainerProps = {

};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
  
  `,
});

const LayoutStyles = breakpoints({
  base: css`
    padding-top: ${theme.UNIT(4)};
    
    .shp--page-half-banner__title {
      font-family: ${theme.FONT('sans')};
      font-size: 0.75em;
      font-weight: 600;
      color: ${theme.COLOR(GESDA_PURPLE_DARK)};
    }
  `,
  desktopFullHD: css`
    .shp--page-half-banner__title {
      font-size: 1.1em;
    }
  `,
});

const HeaderStyles = breakpoints({
  base: css`
    
  `,
});


const RadarChartStyles = breakpoints({
  base: css`

    ${({ isInteractive }: RadarProps ) => !isInteractive && css`
      margin-top: ${theme.UNIT(3)};
      opacity: 0.4;
      filter: saturate(0.4);
    `}
  `,
});

const DownloadButtonStyles = breakpoints({
  base: css`
    max-width: 460px;
  `,
});

const TabsStyles = breakpoints({
  base: css`
    
  `,
});


// -------- Components -------->

const My = {
  Container: styled.div<ContainerProps>`${ContainerStyles}`,
    Header: styled(SiteHeaderLayout)`${HeaderStyles}`,
    Tabs: styled(ExplorerTabs)`${TabsStyles}`,
      Layout: styled(ExplorerEntityDetailsLayout)`${LayoutStyles}`,
      Radar: styled(RadarChart)<RadarProps>`${RadarChartStyles}`,

      DownloadButton: styled(DownloadButton)`${DownloadButtonStyles}`,
};

export const PageExplorerLayout: Shapeable.FC<PageExplorerLayoutProps> = (props) => {
  const { className, children, entity, bannerProps, showRadar, isAsideSticky, ...explorerEntityDetailsProps } = props;

  const radar = useRadar();

  const t = useLang();

  let initialViewBox: SvgCanvasViewBoxDefinition;
  const trend = entity.trend;

  if (trend) {
    initialViewBox = radar.viewboxPresets?.[trend.slug];
  }

  const hasFile = !!entity.files.length
  const url = entity.files[0]?.file?.url || "/pdf";
  const downloadUrl = url.replace(/upload\//, 'upload/fl_attachment/');

  const afterIntroMeta = hasFile ? (
    <My.DownloadButton shape='rounded' href={downloadUrl} colors={{ text: 'dark', border: 'dark', fill: 'dark', hover: { text: 'sub', border: 'sub', fill: 'sub' } }}>
      {t('Download as Extended Version PDF')}
    </My.DownloadButton>
  ) : null;

  return (
    <ExplorerProvider>
    {/* <BackButtonEntityProvider value={entity.parent}> */}
    <ActiveEntityProvider value={entity.parent}>
    <My.Container>
      <ComponentPropsProvider value={{
        EntityBanner: {
          variant: 'image',
          showSubheader: true,
          useBackgroundColor: false,
          entityValueProps: { showImage: false, showOutlineNumber: false }
        },
        PageViewLayout: {
          afterIntro: afterIntroMeta
        }
      }}>
      <My.Header />
      <SliceLayoutBoundary boundary='none' >
        <My.Layout 
          entity={entity}
          className={cls.name(className)}
          {...explorerEntityDetailsProps}
          asideMaxWidth={800}
          isAsideSticky={isAsideSticky}
          stickyTopOffset={95}
          asideIsOffset={false}
          showAsideHeader={false}
          
          dataVizMain={
            <RadarChartMain />
          }

          dataViz={
            showRadar && (
              !initialViewBox ? 
              <RadarChart initialY={10} /> :
              <My.Radar 
                fadeSize={!!initialViewBox && 44} 
                initialViewBox={initialViewBox} 
                isInteractive={!initialViewBox}
              />
            )
          }
          metaHead={
            <PageMetaLayout
              entity={entity} />
          }
        />
        </SliceLayoutBoundary>
      </ComponentPropsProvider>
    </My.Container>
    </ActiveEntityProvider>
    {/* </BackButtonEntityProvider> */}
    </ExplorerProvider>
  )
};

PageExplorerLayout.defaultProps = PageExplorerLayoutDefaultProps;
PageExplorerLayout.cls = cls;